import { createContext, FC } from 'react'
import { Outlet } from 'react-router-dom'

import { PageContentLayout } from '@layouts/PageContentLayout'

import { EditConfigProvider } from '@providers/EditConfigProvider'

import { ENTITY, ENTITY_CONFIG_TYPE, VIEW_ACTION_COLUMNS, VIEW_COLUMNS } from '@constants'

import { useEntityCreateOrEdit } from './hooks'
import { PageContextType } from './types'

export const PageContext = createContext<PageContextType>({} as PageContextType)

type EntityCreateOrEditProps = {
  type: ENTITY
}

export const EntityCreateOrEdit: FC<EntityCreateOrEditProps> = ({ type }) => {
  const { state, data, handlers, mutations } = useEntityCreateOrEdit({ type })
  const { currentEntity, currentRow, showDialog, modalType } = state

  const {
    entityCode,
    entityFormCode,
    objectCode,
    objects,
    object,
    bindingValues,
    bindingValuesTypes,
    bindingValuesColumnsTypes,
    bindingValuesColumns,
    bindingValuesAction,
    bindingValuesActionEvents,
    actionRows,
    mainRows,
    entityIsLoading,
    entityUpdateLoading,
    entityCreateLoading,
    isSuccessCreate,
    isSuccessUpdate,
    initialColumnHeaderStyles,
    initialColumnTextStyles,
    methods,
    watchUseParameters,
    watchQuickSearch,
    watchWrapRowText,
    watchHeaderHeight,
    watchHeaderGroupHeight,
    watchWrapHeaderText,
    isDirtyUseParametersModal,
    createAllColumnsLoading,
    deleteAllColumnsLoading,
    createMandatoryColumnsLoading,
    watchParameterShowSA,
    isValidCreateOrEditForm,
    isActionLoading,
  } = data

  const {
    handleSetModalProperty,
    handleSetObjectCode,
    handleAddEntity,
    handleAddColumn,
    handleDeleteColumn,
    handleDeleteAction,
    handleEditColumn,
    handleCloseModal,
    handleOpenDialog,
    handleCancel,
    handleSetModelCode,
    handleUpdateColumnTitle,
    handleCreateAllColumns,
    handleDeleteAllColumns,
  } = handlers

  const { createAction, editAction, updateColumnOrder, updateVisibilityColumn } = mutations

  const contextValue = {
    entityCode,
    entityFormCode,
    objectCode: objectCode || '',
    currentEntity,
    entityIsLoading,
    entityCreateLoading: entityUpdateLoading || entityCreateLoading,
    currentRow,
    showDialog,
    modalType,
    isSuccessAddModel: isSuccessUpdate || isSuccessCreate,
    objects,
    rawObjectFields: object?.fields,
    objectCommands: object?.commands,
    bindingValues,
    bindingValuesTypes,
    bindingValuesColumnsTypes,
    bindingValuesColumns,
    bindingValuesAction,
    bindingValuesActionEvents,
    actionTable: { rows: actionRows, columns: VIEW_ACTION_COLUMNS },
    mainTable: { rows: mainRows, columns: VIEW_COLUMNS },
    addEntity: handleAddEntity,
    addColumn: handleAddColumn,
    editColumn: handleEditColumn,
    createAction,
    editAction,
    isActionLoading,
    updateColumnOrder,
    updateVisibilityColumn,
    deleteColumn: handleDeleteColumn,
    deleteAction: handleDeleteAction,
    onCancel: handleCancel,
    onSetObjectCode: handleSetObjectCode,
    onCloseModal: handleCloseModal,
    onShowDialog: handleOpenDialog,
    onSetModalProperty: handleSetModalProperty,
    onSetModelCode: handleSetModelCode,
    initialColumnHeaderStyles,
    initialColumnTextStyles,
    methods,
    watchUseParameters,
    watchQuickSearch,
    watchWrapRowText,
    watchHeaderHeight,
    watchHeaderGroupHeight,
    watchWrapHeaderText,
    watchParameterShowSA,
    isDirtyUseParametersModal,
    handleUpdateColumnTitle,
    handleCreateAllColumns,
    handleDeleteAllColumns,
    createAllColumnsLoading,
    deleteAllColumnsLoading,
    createMandatoryColumnsLoading,
    isValidCreateOrEditForm,
    entityType: type,
  }

  return (
    <PageContext.Provider value={contextValue}>
      <EditConfigProvider entityType={ENTITY_CONFIG_TYPE.VIEWS}>
        <PageContentLayout>
          <Outlet />
        </PageContentLayout>
      </EditConfigProvider>
    </PageContext.Provider>
  )
}
